// src/services/RoomService.js
//
// PERF PASS — fixes the two data-layer causes of slow room lists:
//
//   1. N+1 CREATOR HYDRATION — _hydrateRooms fetched the creator PER ROW
//      (20 rooms by one creator = 20 identical creatorService.getById
//      calls, repeated on every refresh). Now: creator ids are deduped
//      per batch AND cached module-wide for 60s, so a studio refresh
//      costs 1 rooms query + at most 1 creator query.
//
//   2. UNBOUNDED PAYLOAD — select('*') pulled base64 thumbnails for up
//      to 200 rows. New getStudioRooms() fetches all live+scheduled rooms
//      (always few) plus a PAGINATED slice of ended rooms with a server
//      count, so the studio loads 6 past cards and "Show more" pages in
//      the rest.
//
// NOTE (still recommended post-launch): thumbnails are base64 data URLs
// stored in the row (~150–400 KB each). Pagination bounds the pain, but
// moving them to Supabase Storage and storing URLs fixes it everywhere
// (Discover, creator pages) at once.
//
// READS go directly against Supabase using the anon key (allowed by the
// SELECT policy on `rooms`).
//
// WRITES go through Edge Functions (create-room, update-room, delete-room)
// via supabase.functions.invoke(). The caller must provide a wallet
// context: { walletAddress, signMessage }.
//
// Field mapping (DB snake_case ↔ UI camelCase) lives in `_dbToUi` /
// `_uiToDb`. Prices are USD cents (ticketPriceCents); a legacy wei price is read for display only.

import { supabase, isDemoMode } from '../lib/supabase';
import { creatorService } from './CreatorService';
import { mockRooms } from '../data/mockRooms';
import { formatEther } from 'viem';
import { isRoomPast } from '../utils/roomUtils';
import { normalizeRoomUpdates } from '../lib/roomEdit';

// ============================================
// FIELD MAPPING HELPERS
// ============================================

/** Mirror of SQL room_access_class (the server decides; this only labels). */
export function roomAccessClass(row) {
  if (!row) return 'free';
  if (row.access_mode === 'open') return 'open';
  if (Number(row.ticket_price_cents || 0) > 0) return 'priced';
  try { if (row.ticket_price_wei != null && BigInt(row.ticket_price_wei) > 0n) return 'unpriced'; } catch { return 'unpriced'; }
  return 'free';
}

function _dbToUi(row, creator) {
  if (!row) return null;

  const totalSeats = row.total_seats;
  const seatsSold = row.seats_sold || 0;
  const seatsReserved = row.seats_reserved || 0; // analytics only — not availability
  const seatsLeft = totalSeats != null ? Math.max(0, totalSeats - seatsSold) : null;

  let ticketPrice = null;
  if (row.ticket_price_wei != null && row.ticket_price_wei !== '0') {
    try {
      ticketPrice = parseFloat(formatEther(BigInt(row.ticket_price_wei)));
    } catch {
      ticketPrice = null;
    }
  }

  let scheduledTime = null;
  if (row.is_instant) {
    scheduledTime = 'Now';
  } else if (row.scheduled_at) {
    try {
      scheduledTime = new Date(row.scheduled_at).toLocaleDateString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      scheduledTime = null;
    }
  }

  return {
    id: row.id,
    creatorId: row.creator_id,
    title: row.title,
    description: row.description,
    thumbnail: row.thumbnail,
    status: row.status,

    startedAt: row.started_at,
    scheduledAt: row.scheduled_at,
    scheduledTime,
    endedAt: row.ended_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,

    duration: row.duration_minutes,

    accessMode: row.access_mode,
    minXPassRequired: row.min_xpass_required,
    ticketPrice,
    ticketPriceWei: row.ticket_price_wei,
    // tickets are sold with the Lunexx balance in USD cents; the same
    // classification as SQL room_access_class (open / free / priced /
    // unpriced = a legacy ETH price that is no longer for sale)
    ticketPriceCents: row.ticket_price_cents ?? null,
    priceRevision: row.price_revision ?? 0,
    // 'credits' (sold from the balance, priced in USD) or 'legacy' (sold on
    // chain or free; never priced in USD). Unknown is treated as legacy.
    paymentOrigin: row.payment_origin ?? null,
    replayPriceCents: row.replay_price_cents ?? null,
    replayPriceRevision: row.replay_price_revision ?? 0,
    accessClass: roomAccessClass(row),
    totalSeats,
    seatsSold,
    seatsLeft,

    onChainId: row.on_chain_id,
    contractRegisteredAt: row.contract_registered_at,
    contractRegisteredTxHash: row.contract_registered_tx_hash,

    streamUrl: row.stream_url,
    streamSource: row.stream_source,
    viewers: row.viewers || 0,
    peakViewers: row.peak_viewers || 0,

    isInstant: row.is_instant,

    // ── Replay (P7.5) ──
    // _dbToUi is a WHITELIST: a column absent from here never reaches the
    // UI, however faithfully the query selected it. These are undefined
    // until replays.sql is applied, which is what lets replay mode simply
    // not render rather than throw.
    //
    // replayVideoGuid is deliberately ABSENT. The full recording's GUID is
    // paid media and must never be in a public row read; the client learns
    // only that a replay EXISTS (replayStatus) and gets a signed, expiring
    // URL from the `replays` edge function after an entitlement check.
    replayStatus: row.replay_status ?? null,
    replayDurationS: row.replay_duration_s ?? null,
    replayPublishedAt: row.replay_published_at ?? null,
    replayPriceWei: row.replay_price_wei ?? null,
    hasPreview: !!row.preview_video_guid,

    creator: creator || null,
  };
}

// the editor's / any caller's fields -> the Edge function body (src/lib/roomEdit.js)
const _normalizeRoomData = normalizeRoomUpdates;

/** a cleared replay that create-room could not take off sale (012-01): the
 *  room exists; the caller tells the creator to clear it in Edit */
const _withReplayNotice = (room, res) =>
  (room && res?.replay_cleared === false ? { ...room, replayNotice: res.replay_message || 'The replay is still for sale.' } : room);

// ============================================
// CREATOR CACHE — kills the N+1 hydration
// ============================================
// Rooms lists constantly re-hydrate the same handful of creators
// (in the studio: exactly one). Cache per-id for 60s so silent
// realtime refreshes don't refetch an unchanged creator profile.

const CREATOR_CACHE_TTL_MS = 60_000;
const _creatorCache = new Map(); // creatorId -> { creator, at }

async function _getCreatorCached(creatorId) {
  if (!creatorId) return null;

  const hit = _creatorCache.get(creatorId);
  if (hit && Date.now() - hit.at < CREATOR_CACHE_TTL_MS) {
    return hit.creator;
  }

  const result = await creatorService.getById(creatorId);
  const creator = result?.data ?? result ?? null;

  // Cache misses too (creator=null) — prevents hammering the DB for a
  // deleted creator on every refresh; TTL lets it recover.
  _creatorCache.set(creatorId, { creator, at: Date.now() });
  return creator;
}

/** Call after editing a creator profile so lists pick up changes early. */
export function invalidateCreatorCache(creatorId = null) {
  if (creatorId) _creatorCache.delete(creatorId);
  else _creatorCache.clear();
}

// ============================================
// EDGE FUNCTION CALLER — uses supabase.functions.invoke()
// ============================================
//
// invoke() handles the Authorization header automatically using whatever
// session/anon key the supabase client is configured with. It also
// returns a structured { data, error } response.

async function _invokeEdge(name, body, authToken = null) {
  const invokeOptions = { body };

  if (authToken) {
    invokeOptions.headers = {
      'X-Lunexx-Auth': `Bearer ${authToken}`,
    };
  }

  const { data, error } = await supabase.functions.invoke(name, invokeOptions);

  if (error) {
    let message = error.message || `${name} failed`;
    let status;
    let details;

    if (error.context && typeof error.context.json === 'function') {
      try {
        details = await error.context.json();
        if (details?.error) message = details.error;
      } catch {
        // body wasn't JSON, fall through to default message
      }
      status = error.context.status;
    }

    const err = new Error(message);
    err.status = status;
    err.details = details;
    err.original = error;
    throw err;
  }

  return data;
}

// =================================================================
// HELPER: resolve auth from third argument.
//
// SIWE-session JWT is the ONLY auth scheme. The legacy per-action
// wallet-signature path (_buildSignedMessage/_safeSign/walletCtx) was
// removed in P1.1 — it caused extra signature prompts and duplicated
// the SIWE session. Callers pass { authToken } (see StudioRooms.getAuth).
// =================================================================

function _resolveAuth(authOrToken, methodName) {
  const authToken =
    typeof authOrToken === 'string'
      ? authOrToken
      : (authOrToken && typeof authOrToken.authToken === 'string' ? authOrToken.authToken : null);

  if (!authToken) {
    throw new Error(`${methodName}: session auth required (pass { authToken } from ensureAuth())`);
  }

  return { authToken };
}

const CALL_STREAM_SOURCES = [
  'agent_call',
  'human_call',
];

const isCallRoom = (room) => {
  const streamSource =
    room?.stream_source ??
    room?.streamSource ??
    null;

  // NULL is allowed because legacy livestream rooms
  // may predate the stream_source column.
  return CALL_STREAM_SOURCES.includes(
    streamSource,
  );
};

// ============================================
// SERVICE
// ============================================

class RoomService {
  constructor() {
    this.mockRooms = mockRooms;
  }

  async _hydrateRoom(row) {
    if (!row) return null;
    const creator = await _getCreatorCached(row.creator_id);
    return _dbToUi(row, creator);
  }

  // Deduped batch hydration: N rooms by 1 creator = 1 creator fetch
  // (0 on cache hit), not N. This was the studio's N+1.
  async _hydrateRooms(rows) {
    if (!rows || rows.length === 0) return [];

    const uniqueIds = [...new Set(rows.map(r => r.creator_id).filter(Boolean))];
    const creators = await Promise.all(uniqueIds.map(id => _getCreatorCached(id)));
    const creatorById = new Map(uniqueIds.map((id, i) => [id, creators[i]]));

    return rows
      .map(row => _dbToUi(row, creatorById.get(row.creator_id)))
      .filter(r => r && r.creator);
  }

  // ============================================
  // READ OPERATIONS — direct Supabase
  // ============================================

  async getAllRooms(filters = {}) {
    if (isDemoMode) return this._mockGetAll(filters);

    let query = supabase
      .from('rooms')
      .select('*')
      .limit(100);

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.creatorId) {
      query = query.eq('creator_id', filters.creatorId);
    }

    // Public discovery queries must never expose private call rooms.
    if (!filters.includePrivate) {
      query = query.neq('access_mode', 'private');
    }

    query = query.order('created_at', {
      ascending: false,
    });

    const { data, error } = await query;

    if (error) {
      console.error(
        '[RoomService] getAllRooms failed:',
        error,
      );
      return [];
    }

    return this._hydrateRooms(data);
  }

  async getRoomById(roomId) {
    if (isDemoMode) return this._mockGetById(roomId);

    const { data, error } = await supabase
      .from('rooms').select('*')
      .eq('id', roomId)
      .maybeSingle();

    if (error) {
      console.error('[RoomService] getRoomById failed:', error);
      return null;
    }
    return this._hydrateRoom(data);
  }

  // ----------------------------------------------------------------
  // BATCH FETCH — one query for N rooms (vs. N getRoomById calls).
  // Optional statuses filter pushes the "upcoming only" cut to the
  // server, so ended rooms' base64 thumbnails are never downloaded
  // just to be discarded client-side.
  // ----------------------------------------------------------------
  async getRoomsByIds(roomIds, { statuses = null } = {}) {
    const ids = [...new Set((roomIds || []).filter(Boolean))];
    if (ids.length === 0) return [];

    if (isDemoMode) {
      const rooms = await Promise.all(ids.map((id) => this._mockGetById(id)));
      const found = rooms.filter(Boolean);
      return statuses ? found.filter((r) => statuses.includes(r.status)) : found;
    }

    let query = supabase.from('rooms').select('*').in('id', ids);
    if (statuses && statuses.length) query = query.in('status', statuses);

    const { data, error } = await query;
    if (error) {
      console.error('[RoomService] getRoomsByIds failed:', error);
      return [];
    }
    return this._hydrateRooms(data);
  }

  async getLiveRooms() {
    return this.getAllRooms({ status: 'live' });
  }

  // Count only — head: true sends no rows back. Discover asks this on every
  // page load to decide whether the Live tab exists, and getLiveRooms()
  // would pull select('*') including the base64 thumbnails documented at
  // the top of this file (~150-400 KB each) for a tab the visitor may
  // never open. Same filters as getLiveRooms so the two can never disagree
  // about what counts as live.
  async getLiveRoomCount() {
    if (isDemoMode) {
      const rooms = await this.getLiveRooms();
      return rooms.length;
    }

    const { count, error } = await supabase
      .from('rooms')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'live')
      .neq('access_mode', 'private');

    if (error) {
      console.error('[RoomService] getLiveRoomCount failed:', error);
      // A failed count must not delete the tab. Unknown is not zero.
      return null;
    }
    return count || 0;
  }

  async getScheduledRooms({
    limit = 100,
    includeCalls = false,
  } = {}) {
    if (isDemoMode) {
      const rooms = await this._mockGetAll({
        status: 'scheduled',
      });

      const visibleRooms = includeCalls
        ? rooms
        : rooms.filter(
          (room) => !isCallRoom(room),
        );

      return visibleRooms
        .sort(
          (a, b) =>
            new Date(
              a.scheduledAt ??
              a.scheduled_at ??
              0,
            ) -
            new Date(
              b.scheduledAt ??
              b.scheduled_at ??
              0,
            ),
        )
        .slice(0, limit);
    }

    // Only future (or just-started, within a 2h grace window) events.
    // Stale `scheduled` rows are never flipped to `ended` server-side, so
    // without this date filter dead events would be listed forever (P1.2).
    // Rooms with NULL scheduled_at keep their old behavior (listed last).
    const cutoff = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('rooms')
      .select('*')
      .eq('status', 'scheduled')
      .or(`scheduled_at.gte.${cutoff},scheduled_at.is.null`)
      .order('scheduled_at', {
        ascending: true,
        nullsFirst: false,
      })
      .limit(limit);

    if (error) {
      console.error(
        '[RoomService] getScheduledRooms failed:',
        error,
      );

      throw error;
    }

    const rows = data ?? [];

    const visibleRooms = includeCalls
      ? rows
      : rows.filter(
        (room) => !isCallRoom(room),
      );

    return this._hydrateRooms(visibleRooms);
  }
  async getEndedRooms() {
    if (isDemoMode) {
      const rooms = await this._mockGetAll({ status: 'ended' });
      return rooms.sort((a, b) => new Date(b.endedAt) - new Date(a.endedAt));
    }
    const { data, error } = await supabase
      .from('rooms').select('*')
      .eq('status', 'ended')
      .order('ended_at', { ascending: false })
      .limit(100);
    if (error) { console.error('[RoomService] getEndedRooms failed:', error); return []; }
    return this._hydrateRooms(data);
  }

  async getRoomsByCreatorId(
    creatorId,
    status = null,
    {
      limit = 200,
      offset = 0,
      includeCalls = false,
    } = {},
  ) {
    if (!creatorId) {
      return [];
    }

    const filters = {
      creatorId,
    };

    if (status) {
      filters.status = status;
    }

    if (isDemoMode) {
      const rooms = await this._mockGetAll(filters);

      const filteredRooms = includeCalls
        ? rooms
        : rooms.filter(
          (room) => !isCallRoom(room),
        );

      return filteredRooms.slice(
        offset,
        offset + limit,
      );
    }

    let query = supabase
      .from('rooms')
      .select('*')
      .eq('creator_id', creatorId);

    if (status) {
      query = query.eq('status', status);
    }

    /*
     * Creator room lists show broadcasts, not one-to-one calls.
     *
     * Keep NULL stream_source rows because older livestream records may
     * predate the stream_source column. Explicit call-history queries can
     * opt in with { includeCalls: true }.
     */


    if (status === 'scheduled') {
      query = query.order(
        'scheduled_at',
        {
          ascending: true,
          nullsFirst: false,
        },
      );
    } else if (status === 'ended') {
      query = query.order(
        'ended_at',
        {
          ascending: false,
          nullsFirst: false,
        },
      );
    } else {
      query = query.order(
        'created_at',
        {
          ascending: false,
        },
      );
    }

    query = query.range(
      offset,
      offset + limit - 1,
    );

    const { data, error } = await query;

    if (error) {
      console.error(
        '[RoomService] getRoomsByCreatorId failed:',
        {
          creatorId,
          status,
          includeCalls,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        },
      );

      throw error;
    }

    const rows = data ?? [];

    const visibleRooms = includeCalls
      ? rows
      : rows.filter(
        (room) => !isCallRoom(room),
      );

    return this._hydrateRooms(visibleRooms);
  }

  // ----------------------------------------------------------------
  // STUDIO LIST — one round-trip shape for the Broadcast Studio page:
  //   • ALL live + scheduled rooms (always few, always shown)
  //   • a PAGINATED slice of ended rooms (default 6) + server-side
  //     total count, so the UI can render "Show more"
  // Both queries run in parallel; hydration costs at most 1 creator
  // fetch thanks to the cache.
  // ----------------------------------------------------------------
  async getStudioRooms(creatorId, { pastLimit = 6, pastOffset = 0 } = {}) {
    if (isDemoMode) {
      const all = await this._mockGetAll({ creatorId });
      const active = all.filter(r => r.status === 'live' || r.status === 'scheduled');
      const endedAll = all
        .filter(r => r.status === 'ended')
        .sort((a, b) => new Date(b.endedAt) - new Date(a.endedAt));
      return {
        rooms: [...active, ...endedAll.slice(pastOffset, pastOffset + pastLimit)],
        endedTotal: endedAll.length,
      };
    }

    const [activeRes, endedRes] = await Promise.all([
      supabase
        .from('rooms')
        .select('*')
        .eq('creator_id', creatorId)
        .in('status', ['live', 'scheduled'])
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('rooms')
        .select('*', { count: 'exact' })
        .eq('creator_id', creatorId)
        .eq('status', 'ended')
        .order('ended_at', { ascending: false })
        .range(pastOffset, pastOffset + pastLimit - 1),
    ]);

    if (activeRes.error) console.error('[RoomService] getStudioRooms (active) failed:', activeRes.error);
    if (endedRes.error) console.error('[RoomService] getStudioRooms (ended) failed:', endedRes.error);

    const rows = [...(activeRes.data || []), ...(endedRes.data || [])];
    const rooms = await this._hydrateRooms(rows);

    return {
      rooms,
      endedTotal: endedRes.count ?? (endedRes.data?.length || 0),
    };
  }

  async getRoomsByCreatorHandle(handle, status = null) {
    if (!handle) return [];
    const lookup = await creatorService.getCreatorByHandle?.(handle);
    const creator = lookup?.data || lookup;
    if (!creator) return [];
    return this.getRoomsByCreatorId(creator.id, status);
  }

  // Returns the creator's latest public live room.
  async getCreatorLiveRoom(creatorId) {
    if (isDemoMode) {
      const rooms = await this._mockGetAll({
        creatorId,
        status: 'live',
      });

      return (
        rooms.find(
          (room) => room.accessMode !== 'private' && room.access_mode !== 'private',
        ) || null
      );
    }

    const { data, error } = await supabase
      .from('rooms')
      .select('*')
      .eq('creator_id', creatorId)
      .eq('status', 'live')
      .neq('access_mode', 'private')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(
        '[RoomService] getCreatorLiveRoom failed:',
        error,
      );
      return null;
    }

    if (!data) return null;

    // _hydrateRooms is async. Without the await this indexed a Promise,
    // `hydrated[0]` was undefined, and the method returned null for EVERY
    // creator regardless of what the query found — so a creator's Rooms
    // tab could never see them live.
    const hydrated = await this._hydrateRooms([data]);
    return hydrated[0] || null;
  }

  async getCreatorLiveRoomByHandle(handle) {
    if (!handle) return null;
    const lookup = await creatorService.getCreatorByHandle?.(handle);
    const creator = lookup?.data || lookup;
    if (!creator) return null;
    return this.getCreatorLiveRoom(creator.id);
  }

  async getCreatorUpcomingRooms(creatorId) {
    if (!creatorId) {
      return [];
    }

    const rooms = await this.getRoomsByCreatorId(
      creatorId,
      'scheduled',
      {
        includeCalls: false,
      },
    );

    // "Upcoming" means the future: stale `scheduled` rows are never
    // flipped to `ended` server-side (until the expire-rooms cron runs),
    // so a status-only filter surfaced long-dead rooms in every
    // "Upcoming Rooms" rail (same P1.2 bug as MyTickets/Discover).
    return rooms.filter((r) => !isRoomPast(r));
  }
  async getCreatorUpcomingRoomsByHandle(handle) {
    if (!handle) return [];
    const lookup = await creatorService.getCreatorByHandle?.(handle);
    const creator = lookup?.data || lookup;
    if (!creator) return [];
    return this.getCreatorUpcomingRooms(creator.id);
  }
  async getCreatorPastRooms(creatorId) {
    return this.getRoomsByCreatorId(
      creatorId,
      'ended',
    );
  }
  async getCreatorPastRoomsByHandle(handle) {
    if (!handle) return [];
    const lookup = await creatorService.getCreatorByHandle?.(handle);
    const creator = lookup?.data || lookup;
    if (!creator) return [];
    return this.getCreatorPastRooms(creator.id);
  }

  // Lightweight existence check — no row payload, no hydration.
  async isCreatorLive(creatorId) {
    if (isDemoMode) {
      const liveRoom = await this.getCreatorLiveRoom(creatorId);
      return liveRoom !== null;
    }
    const { count, error } = await supabase
      .from('rooms')
      .select('id', { count: 'exact', head: true })
      .eq('creator_id', creatorId)
      .eq('status', 'live')
      .neq('access_mode', 'private');

    if (error) {
      console.error('[RoomService] isCreatorLive failed:', error);
      return false;
    }
    return (count || 0) > 0;
  }

  async isCreatorLiveByHandle(handle) {
    const liveRoom = await this.getCreatorLiveRoomByHandle(handle);
    return liveRoom !== null;
  }

  // ============================================
  // WRITE OPERATIONS — Edge Functions via invoke()
  // ============================================

  async createRoom(roomData, authOrWalletCtx) {
    if (!roomData?.creatorId || !roomData?.title) {
      throw new Error('Creator ID and title are required');
    }

    if (isDemoMode) {
      return this._mockCreate(roomData, { instant: false });
    }

    const { authToken } = _resolveAuth(authOrWalletCtx, 'createRoom');
    const normalized = _normalizeRoomData(roomData);

    const res = await _invokeEdge('create-room', { roomData: normalized }, authToken);
    const room = res?.room;
    if (!room) throw new Error('create-room returned no room');
    this._notifyFollowers('room_scheduled', room.id, authToken);
    return _withReplayNotice(await this._hydrateRoom(room), res);
  }

  /** Fire-and-forget fan-out to Notify subscribers — server-side
   *  idempotent (notification_log), so calling on every create is safe.
   *  Never allowed to fail the creation flow it rides on. */
  _notifyFollowers(kind, roomId, authToken) {
    if (isDemoMode || !roomId || !authToken) return;
    _invokeEdge('notify-followers', { kind, roomId }, authToken)
      .catch((e) => console.warn('[RoomService] notify-followers failed:', e?.message));
  }


  async createInstantLiveRoom(roomData, authOrWalletCtx) {
    if (!roomData?.creatorId || !roomData?.title) {
      throw new Error('Creator ID and title are required');
    }
    if (isDemoMode) return this._mockCreate(roomData, { instant: true });

    const { authToken } = _resolveAuth(authOrWalletCtx, 'createInstantLiveRoom');

    // Lightweight count check (was a full fetch+hydrate of live rooms)
    const alreadyLive = await this.isCreatorLive(roomData.creatorId);
    if (alreadyLive) throw new Error('Creator already has an active live room');

    const normalized = _normalizeRoomData({ ...roomData, isInstant: true });
    if (!normalized.scheduledAt) normalized.scheduledAt = new Date().toISOString();

    const res = await _invokeEdge('create-room', { roomData: normalized }, authToken);
    const room = res?.room;
    if (!room) throw new Error('create-room returned no room');
    this._notifyFollowers('room_live', room.id, authToken);
    return _withReplayNotice(await this._hydrateRoom(room), res);
  }

  async updateRoom(roomId, updates, authOrWalletCtx) {
    if (!roomId) throw new Error('roomId is required');
    if (isDemoMode) return this._mockUpdate(roomId, updates);

    const { authToken } = _resolveAuth(authOrWalletCtx, 'updateRoom');

    const normalized = _normalizeRoomData(updates);

    // Forward fields that bypass _normalizeRoomData (status flips etc.)
    const forward = ['status', 'startedAt', 'endedAt', 'streamUrl', 'streamSource', 'viewers', 'peakViewers'];
    for (const f of forward) {
      if (updates[f] !== undefined) normalized[f] = updates[f];
    }

    if (Object.keys(normalized).length === 0) {
      return this.getRoomById(roomId);
    }

    const { room } = await _invokeEdge(
      'update-room',
      { roomId, updates: normalized },
      authToken
    );
    if (!room) throw new Error('update-room returned no room');
    return this._hydrateRoom(room);
  }

  // ============================================
  // SEAT RESERVATIONS (free RSVP — holders on paid rooms, anyone on free)
  // Server: the reserve-seat edge function (JWT + on-chain holder check,
  // atomic via reserve_room_seat / cancel_room_seat SQL functions)
  // ============================================

  async reserveSeat(roomId, authOrWalletCtx) {
    if (!roomId) throw new Error('roomId is required');
    const { authToken } = _resolveAuth(authOrWalletCtx, 'reserveSeat');
    if (!authToken) throw new Error('reserveSeat requires session auth');
    return _invokeEdge('reserve-seat', { roomId, action: 'reserve' }, authToken);
  }

  async cancelReservation(roomId, authOrWalletCtx) {
    if (!roomId) throw new Error('roomId is required');
    const { authToken } = _resolveAuth(authOrWalletCtx, 'cancelReservation');
    if (!authToken) throw new Error('cancelReservation requires session auth');
    return _invokeEdge('reserve-seat', { roomId, action: 'cancel' }, authToken);
  }


  // Fire-and-forget poke at the escrow attestor (RoomsV2). The function
  // derives everything from DB + chain state itself, so a failed or
  // duplicate poke is harmless; a cron sweep can pick up misses.
  _pokeAttestation(roomId) {
    try {
      supabase.functions.invoke('attest-room', { body: { roomId } })
        .catch((e) => console.warn('[RoomService] attest-room poke failed:', e?.message));
    } catch { /* never block the stream flow on attestation */ }
  }

  async startLiveRoom(roomId, authOrWalletCtx, options = {}) {
    if (isDemoMode) return this._mockStartLive(roomId, options);

    const updates = {
      status: 'live',
      startedAt: new Date().toISOString(),
      streamSource: options.source || 'camera',
    };
    if (options.streamUrl) updates.streamUrl = options.streamUrl;

    const result = await this.updateRoom(roomId, updates, authOrWalletCtx);
    this._pokeAttestation(roomId); // lock refunds: stream is live
    return result;
  }

  async endLiveRoom(roomId, authOrWalletCtx) {
    if (isDemoMode) return this._mockEndLive(roomId);

    const result = await this.updateRoom(roomId, {
      status: 'ended',
      endedAt: new Date().toISOString(),
      viewers: 0,
    }, authOrWalletCtx);
    this._pokeAttestation(roomId); // unlock creator claim (if verified)
    return result;
  }

  async startRoom(roomId, auth, streamUrl = null) {
    return this.startLiveRoom(roomId, auth, { streamUrl });
  }
  async endRoom(roomId, auth) {
    return this.endLiveRoom(roomId, auth);
  }

  async deleteRoom(roomId, authOrWalletCtx) {
    if (!roomId) throw new Error('roomId is required');
    if (isDemoMode) return this._mockDelete(roomId);

    const { authToken } = _resolveAuth(authOrWalletCtx, 'deleteRoom');

    await _invokeEdge('delete-room', { roomId }, authToken);
    return true;
  }

  async updateViewerCount(roomId, count, authOrWalletCtx) {
    if (isDemoMode) return this._mockUpdateViewers(roomId, count);

    const safeCount = Math.max(0, Math.floor(count || 0));
    return this.updateRoom(roomId, { viewers: safeCount }, authOrWalletCtx);
  }

  async bookSeat(roomId, userId = null) {
    if (isDemoMode) return this._mockBookSeat(roomId);
    throw new Error('bookSeat is not yet wired to Edge Functions. Use the ticket purchase flow.');
  }

  // ============================================
  // HELPERS
  // ============================================

  getRoomThumbnail(room, creator) {
    if (room?.thumbnail) return room.thumbnail;
    if (creator?.liveImage) return creator.liveImage;
    if (creator?.banner) return creator.banner;
    return 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=80&w=1920&h=1080&auto=format&fit=crop';
  }

  // ============================================
  // DEMO MODE FALLBACKS (unchanged)
  // ============================================

  async _mockGetAll(filters = {}) {
    let results = [...this.mockRooms];
    if (filters.status) results = results.filter(r => r.status === filters.status);
    if (filters.creatorId) results = results.filter(r => r.creatorId === filters.creatorId);
    const hydrated = await Promise.all(
      results.map(async (room) => {
        const { data: creator } = await creatorService.getById(room.creatorId);
        return { ...room, creator, thumbnail: this.getRoomThumbnail(room, creator) };
      })
    );
    return hydrated;
  }

  async _mockGetById(roomId) {
    const room = this.mockRooms.find(r => r.id === roomId);
    if (!room) return null;
    const { data: creator } = await creatorService.getById(room.creatorId);
    return { ...room, creator, thumbnail: this.getRoomThumbnail(room, creator) };
  }

  async _mockCreate(roomData, { instant }) {
    if (instant) {
      const existingLive = this.mockRooms.find(r =>
        r.creatorId === roomData.creatorId && r.status === 'live'
      );
      if (existingLive) throw new Error('Creator already has an active live room');
    }
    const newRoom = {
      id: `room-${Date.now()}`,
      creatorId: roomData.creatorId,
      title: roomData.title,
      description: roomData.description || '',
      thumbnail: roomData.thumbnail || null,
      status: 'scheduled',
      viewers: 0,
      startedAt: null,
      scheduledTime: instant ? 'Now' : roomData.scheduledTime,
      scheduledAt: roomData.scheduledAt || new Date().toISOString(),
      endedAt: null,
      accessTier: roomData.accessTier || 'free',
      ticketPrice: roomData.ticketPrice || null,
      minTokens: roomData.minTokens || 0,
      seatsLeft: roomData.totalSeats || (instant ? null : 50),
      totalSeats: roomData.totalSeats || (instant ? null : 50),
      duration: roomData.duration || null,
      streamUrl: roomData.streamUrl || null,
      createdAt: new Date().toISOString(),
      isInstant: !!instant,
    };
    this.mockRooms.push(newRoom);
    return this._mockGetById(newRoom.id);
  }

  async _mockStartLive(roomId, options) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    const room = this.mockRooms[index];
    if (room.status === 'live') throw new Error('Room is already live');
    if (room.status === 'ended') throw new Error('Cannot restart an ended room');
    const existingLive = this.mockRooms.find(r =>
      r.creatorId === room.creatorId && r.status === 'live'
    );
    if (existingLive && existingLive.id !== roomId) {
      throw new Error('Creator already has an active live room');
    }
    this.mockRooms[index] = {
      ...room,
      status: 'live',
      startedAt: new Date().toISOString(),
      viewers: 0,
      streamSource: options?.source || 'camera',
    };
    return this._mockGetById(roomId);
  }

  async _mockEndLive(roomId) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    if (this.mockRooms[index].status !== 'live') throw new Error('Room is not currently live');
    this.mockRooms[index] = {
      ...this.mockRooms[index],
      status: 'ended',
      endedAt: new Date().toISOString(),
      viewers: 0,
    };
    return this._mockGetById(roomId);
  }

  async _mockUpdate(roomId, updates) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    const { id, creatorId, createdAt, ...allowed } = updates;
    this.mockRooms[index] = { ...this.mockRooms[index], ...allowed };
    return this._mockGetById(roomId);
  }

  async _mockUpdateViewers(roomId, count) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    this.mockRooms[index] = { ...this.mockRooms[index], viewers: Math.max(0, count || 0) };
    return this._mockGetById(roomId);
  }

  async _mockDelete(roomId) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    if (this.mockRooms[index].status === 'live') throw new Error('Cannot delete a live room');
    this.mockRooms.splice(index, 1);
    return true;
  }

  async _mockBookSeat(roomId) {
    const index = this.mockRooms.findIndex(r => r.id === roomId);
    if (index === -1) throw new Error('Room not found');
    const room = this.mockRooms[index];
    if (room.status !== 'scheduled') throw new Error('Room is not accepting bookings');
    if (room.seatsLeft <= 0) throw new Error('Room is sold out');
    this.mockRooms[index] = { ...room, seatsLeft: room.seatsLeft - 1 };
    return this._mockGetById(roomId);
  }
}

export const roomService = new RoomService();
export default roomService;